/**
 * Utility functions for managing lead generation status in localStorage
 * This allows lead generation completion/decline status to persist across page reloads
 * Lead generation persistence is only enabled for specific allowed client user IDs
 */

import { LEAD_GEN_PERSISTENCE_ALLOWED_CLIENTS } from '@/constants';

/**
 * Check if lead generation persistence is allowed for the given client user ID
 * @param {string} clientUserId - The client user ID to check
 * @returns {boolean} Whether persistence is allowed for this client
 */
export const isLeadGenPersistenceAllowed = (clientUserId) => {
  if (!clientUserId) return false;
  return LEAD_GEN_PERSISTENCE_ALLOWED_CLIENTS.includes(clientUserId);
};

/**
 * Check if lead generation persistence is allowed based on chatbot owner
 * @param {Object} config - The chatbot configuration object
 * @returns {boolean} Whether persistence is allowed for this chatbot's owner
 */
export const isLeadGenPersistenceAllowedForConfig = (config) => {
  if (!config) return false;

  // Check if the chatbot owner (user_id) is in the allowed clients list
  return config.user_id && LEAD_GEN_PERSISTENCE_ALLOWED_CLIENTS.includes(config.user_id);
};

/**
 * Get the localStorage key for a specific chatbot's lead generation status
 * @param {string} kbId - The knowledge base ID
 * @returns {string} The localStorage key
 */
export const getLeadGenStorageKey = (kbId) => `leadgen-status-${kbId}`;

/**
 * Get stored lead generation status from localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {Object} Object with completed and declined boolean flags
 */
export const getStoredLeadGenStatus = (kbId, configOrClientUserId = null) => {
  if (!kbId) return { completed: false, declined: false };

  let persistenceAllowed = false;

  // Check if the second parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    persistenceAllowed = isLeadGenPersistenceAllowedForConfig(configOrClientUserId);
  }
  // Fallback to old approach for backward compatibility (client user ID)
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceAllowed = isLeadGenPersistenceAllowed(configOrClientUserId);
  }

  if (!persistenceAllowed) {
    return { completed: false, declined: false };
  }

  try {
    const stored = localStorage.getItem(getLeadGenStorageKey(kbId));
    return stored ? JSON.parse(stored) : { completed: false, declined: false };
  } catch (error) {
    console.warn('Failed to parse lead generation status from localStorage:', error);
    return { completed: false, declined: false };
  }
};

/**
 * Store lead generation status to localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object} status - Object with completed and declined boolean flags
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const setStoredLeadGenStatus = (kbId, status, configOrClientUserId = null) => {
  if (!kbId) return;

  let persistenceAllowed = false;

  // Check if the third parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    persistenceAllowed = isLeadGenPersistenceAllowedForConfig(configOrClientUserId);
  }
  // Fallback to old approach for backward compatibility (client user ID)
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceAllowed = isLeadGenPersistenceAllowed(configOrClientUserId);
  }

  if (!persistenceAllowed) {
    return;
  }

  try {
    localStorage.setItem(getLeadGenStorageKey(kbId), JSON.stringify(status));
  } catch (error) {
    console.warn('Failed to store lead generation status to localStorage:', error);
  }
};

/**
 * Clear stored lead generation status from localStorage
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const clearStoredLeadGenStatus = (kbId, configOrClientUserId = null) => {
  if (!kbId) return;

  let persistenceAllowed = false;

  // Check if the second parameter is a config object (new approach)
  if (configOrClientUserId && typeof configOrClientUserId === 'object') {
    persistenceAllowed = isLeadGenPersistenceAllowedForConfig(configOrClientUserId);
  }
  // Fallback to old approach for backward compatibility (client user ID)
  else if (configOrClientUserId && typeof configOrClientUserId === 'string') {
    persistenceAllowed = isLeadGenPersistenceAllowed(configOrClientUserId);
  }

  if (!persistenceAllowed) {
    return;
  }

  try {
    localStorage.removeItem(getLeadGenStorageKey(kbId));
  } catch (error) {
    console.warn('Failed to clear lead generation status from localStorage:', error);
  }
};

/**
 * Check if lead generation should be shown based on stored status and configuration
 * @param {string} kbId - The knowledge base ID
 * @param {Object} config - The chatbot configuration
 * @returns {boolean} Whether lead generation should be shown
 */
export const shouldShowLeadGen = (kbId, config) => {
  if (!config?.data_collection) return false;

  // Use the config object for dynamic checking
  const storedStatus = getStoredLeadGenStatus(kbId, config);
  if (storedStatus.completed || storedStatus.declined) return false;

  return config.show_data_collection_on_conversation_begin;
};

/**
 * Reset lead generation status for a new session (optional utility)
 * This can be called when you want to allow lead generation to appear again
 * Note: By default, lead generation status persists across chat resets
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const resetLeadGenForNewSession = (kbId, configOrClientUserId = null) => {
  clearStoredLeadGenStatus(kbId, configOrClientUserId);
};

/**
 * Force clear lead generation status (for admin/debug purposes)
 * This completely removes the stored status, allowing lead gen to appear again
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 */
export const forceResetLeadGen = (kbId, configOrClientUserId = null) => {
  clearStoredLeadGenStatus(kbId, configOrClientUserId);
};

/**
 * Check if user has completed lead generation
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {boolean} Whether lead generation was completed
 */
export const hasCompletedLeadGen = (kbId, configOrClientUserId = null) => {
  const status = getStoredLeadGenStatus(kbId, configOrClientUserId);
  return status.completed;
};

/**
 * Check if user has declined lead generation
 * @param {string} kbId - The knowledge base ID
 * @param {Object|string} configOrClientUserId - The chatbot config object or client user ID (for backward compatibility)
 * @returns {boolean} Whether lead generation was declined
 */
export const hasDeclinedLeadGen = (kbId, configOrClientUserId = null) => {
  const status = getStoredLeadGenStatus(kbId, configOrClientUserId);
  return status.declined;
};
